 
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmojiInput } from '../../../common/utils/CommonUtils';
import { SOURCE_REGISTRY_FORM_PARAM } from '../../constants';
import { ISourceRegistryValidationModel, SourceRegistryFormData } from '../../models';
import { isJson, setFormElementStatus, sourceIntentNameSetter } from '../../utilities/sources/SourceRegistryUtil';

/**
 * Custom hook to handle Source Registry Form Inputs
 */
export const useSourceRegistryForm = () => {
  const [formData, setFormParamValues] = useState(new SourceRegistryFormData());
  const { t } = useTranslation();
  const SOURCE_REGISTRY_ERRORS = {
    requiredSourceRegistryName: t('sourceRegErrors.requiredSourceRegistryName'),
    invalidSourceRegistryName: t('sourceRegErrors.invalidSourceRegistryName'),
    invalidIdentifiers: t('sourceRegErrors.invalidIdentifiers'),
    invalidSourceTopicRegistryName: t('sourceTopicErrorHook.invalidSourceTopicRegistryName'),
    invalidReplyTimeOut: t('sourceTopicErrorHook.invalidReplyTimeOut'),
    invalidReplyTopicName: t('sourceTopicErrorHook.invalidReplyTopicName'),
    jsonTransformation: t('SERErrors.jsonTransformation'),
  };

  const checkValidity = (name: keyof ISourceRegistryValidationModel, value: string) => {
    const expString = formData[name].expression;
    const regExp = new RegExp(expString || '');
    const regExCondition = formData[name] && !regExp.test(value.toLowerCase());
    if (value.length < 1 || regExCondition) {
      setFormParamValues((previousState: ISourceRegistryValidationModel) => setFormElementStatus(previousState, name, value, null, false, true));
    } else {
      setFormParamValues((previousState: ISourceRegistryValidationModel) => setFormElementStatus(previousState, name, value, null, true, true));
    }
  };

  const getTotalIdentifierLen = (formData: ISourceRegistryValidationModel, idnLen: number) => (formData.identifiersArray.value ? formData.identifiersArray.value.length + idnLen + 1 : formData.identifiersArray.value.length + idnLen);
  const getFormParam = (previousState: ISourceRegistryValidationModel, name: string, currLength: number, maxChars: number, value: string) => {
    const keyName = name as keyof ISourceRegistryValidationModel;
    const data = structuredClone(previousState);
    const expString = formData[keyName].expression;
    const regExp = new RegExp(expString || '');
    const regExValid = regExp.test(data[keyName].value);
    const dataName = data[keyName].name;
    const dataValue = data[keyName].value;
    const totalIdentifierLen = getTotalIdentifierLen(formData, dataValue.length);

    if (dataName === SOURCE_REGISTRY_FORM_PARAM.sourceRegistryName && currLength === maxChars) {
      return setFormElementStatus(previousState, name, value, t('source_registry_form.err_max_char_limit', { replace: { maxChars: maxChars - 1 } }), false, true);
    }

    if (!dataValue) {
      return sourceIntentNameSetter(dataName, previousState, name, value, SOURCE_REGISTRY_ERRORS);
    }

    if (totalIdentifierLen > 500 && data[keyName].value && dataName === SOURCE_REGISTRY_FORM_PARAM.identifiers) {
      return setFormElementStatus(previousState, name, value, t('source_registry_form.err_max_char_limit', { replace: { maxChars: 500 } }), false, true);
    }

    switch (dataName) {
      case SOURCE_REGISTRY_FORM_PARAM.sourceRegistryName:
        data[keyName].error = SOURCE_REGISTRY_ERRORS.invalidSourceRegistryName;
        break;
      case SOURCE_REGISTRY_FORM_PARAM.identifiers:
        if (!regExValid) {
          data[keyName].error = SOURCE_REGISTRY_ERRORS.invalidIdentifiers;
        } else {
          data[keyName].error = '';
          data[keyName].isValid = true;
        }
        break;
      case SOURCE_REGISTRY_FORM_PARAM.replyTimeout:
        data[keyName].error = setErrorText(SOURCE_REGISTRY_FORM_PARAM.replyTimeout, regExValid);
        data[keyName].isValid = setIsValid(data[keyName].error);
        break;
      case SOURCE_REGISTRY_FORM_PARAM.replyToTopic:
        data[keyName].error = setErrorText(SOURCE_REGISTRY_FORM_PARAM.replyToTopic, regExValid);
        data[keyName].isValid = setIsValid(data[keyName].error);
        break;
      case SOURCE_REGISTRY_FORM_PARAM.requestSpecJson:
        data[keyName].error = SOURCE_REGISTRY_ERRORS.jsonTransformation;
        break;
      default:
        data[keyName].error = '';
    }
    return data;
  };

  /**
   * Method to set error text
   */
  const setErrorText = (fieldName: string, regExValid: boolean) => {
    let errorText = '';
    if (!regExValid) {
      errorText = fieldName === SOURCE_REGISTRY_FORM_PARAM.replyToTopic
        ? SOURCE_REGISTRY_ERRORS.invalidReplyTopicName : SOURCE_REGISTRY_ERRORS.invalidReplyTimeOut;
    }
    return errorText;
  };

  /**
   * Method to set isValid
   */
  const setIsValid = (errorText: string) => !errorText;

  /**
 * method to handle form inputs
 */
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const keyName = name as keyof ISourceRegistryValidationModel;
    if (isEmojiInput(value)) {
      return;
    }
    const maxChars = event.target.maxLength > -1 ? event.target.maxLength : Number.MAX_SAFE_INTEGER;
    const currLength = event.target.value.length;
    if (formData[keyName].expression) {
      checkValidity(keyName, value);
    }

    const allNumbersRegex = /^[0-9 ]+$/;
    const noEmptyQuotes = ((value.charAt(0) !== '"' && value.charAt(1) !== '"'));
    const onlyNumbers = allNumbersRegex.test(value);
    if (name === SOURCE_REGISTRY_FORM_PARAM.requestSpecJson || name === SOURCE_REGISTRY_FORM_PARAM.responseSpecJson) {
      const isValidStatus = value ? (isJson(value) && JSON.parse(value) !== null && Object.keys(JSON.parse(value)).length > 0 && !Object.keys(JSON.parse(value)).includes('')) : true;
      setFormParamValues((previousState) => {
        const data = { ...previousState };
        data[keyName].isValid = isValidStatus && noEmptyQuotes && !onlyNumbers;
        data[keyName].value = value;
        data[keyName].isTouched = true;
        data[keyName].error = data[keyName].isValid === true ? '' : SOURCE_REGISTRY_ERRORS.jsonTransformation;
        return data;
      });
    }
    setFormParamValues((previousState: ISourceRegistryValidationModel) => getFormParam(previousState, keyName, currLength, maxChars, value));
  };
  return {
    handleInputChange,
    formData,
    setFormParamValues,
    getFormParam
  };
};
